import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  MATouchAlertRule,
  MATouchAlertEvent,
  MATouchAlertStatus,
  WhatsAppAlertStatus,
  AlertStateMachineEntry,
  AlertsState,
  BuyActionWatch
} from '../../types';

const initialState: AlertsState = {
  rules: [],
  events: [],
  runStatus: null,
  whatsappStatus: null,
  alertStates: [],
  watchEntries: [],
  loading: false,
  error: null,
};

const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    // Rules
    setRules: (state, action: PayloadAction<MATouchAlertRule[]>) => {
      state.rules = action.payload;
    },
    addRule: (state, action: PayloadAction<MATouchAlertRule>) => {
      state.rules.unshift(action.payload);
    },
    updateRule: (state, action: PayloadAction<MATouchAlertRule>) => {
      const index = state.rules.findIndex(r => r.id === action.payload.id);
      if (index !== -1) {
        state.rules[index] = action.payload;
      }
    },
    removeRule: (state, action: PayloadAction<string>) => {
      state.rules = state.rules.filter(r => r.id !== action.payload);
    },

    // Events
    setEvents: (state, action: PayloadAction<MATouchAlertEvent[]>) => {
      state.events = action.payload;
    },

    // Run Status
    setRunStatus: (state, action: PayloadAction<MATouchAlertStatus | null>) => {
      state.runStatus = action.payload;
    },

    // WhatsApp
    setWhatsAppStatus: (state, action: PayloadAction<WhatsAppAlertStatus | null>) => {
      state.whatsappStatus = action.payload;
    },

    // Alert State Machine
    setAlertStates: (state, action: PayloadAction<AlertStateMachineEntry[]>) => {
      state.alertStates = action.payload;
    },

    // Buy-Action watchlist (one-shot buy-zone watches)
    setWatchEntries: (state, action: PayloadAction<BuyActionWatch[]>) => {
      state.watchEntries = action.payload;
    },
    addWatch: (state, action: PayloadAction<BuyActionWatch>) => {
      // Upsert by ticker (case-insensitive) so re-adding an existing watch
      // replaces rather than duplicates - mirrors the backend per-(user,ticker)
      // upsert contract.
      const ticker = action.payload.ticker?.trim?.().toUpperCase();
      const filtered = state.watchEntries.filter(
        (w) => w.ticker?.trim?.().toUpperCase() !== ticker
      );
      state.watchEntries = [action.payload, ...filtered];
    },
    removeWatch: (state, action: PayloadAction<string>) => {
      const ticker = action.payload?.trim?.().toUpperCase();
      state.watchEntries = state.watchEntries.filter(
        (w) => w.ticker?.trim?.().toUpperCase() !== ticker
      );
    },

    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Error
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Clear all
    clearAlerts: (state) => {
      state.rules = [];
      state.events = [];
      state.runStatus = null;
      state.whatsappStatus = null;
      state.alertStates = [];
      state.watchEntries = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setRules,
  addRule,
  updateRule,
  removeRule,
  setEvents,
  setRunStatus,
  setWhatsAppStatus,
  setAlertStates,
  setWatchEntries,
  addWatch,
  removeWatch,
  setLoading,
  setError,
  clearAlerts,
} = alertsSlice.actions;

export default alertsSlice.reducer;
